import React, { useEffect } from "react";
import { Controller } from "react-hook-form";
import { useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { OptionType, IDType } from "../../@lib/types";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { Attachment } from "..";
import { DatePicker, useLanguageTranslator } from "../../components";
import dayjs from "dayjs";

export type PolicyInfoFormProps = {
  countryOptions: OptionType<IDType>[];
  themeOptions: OptionType<IDType>[];
  geographicalAreaOptions: (OptionType<IDType> & {
    policyCategoryOptions: OptionType<IDType>[];
  })[];
  disabled?: boolean;
  setImageAttachmentDetails: any;
  imageAttachmentDetails: any;
  documentAttachmentDetails: any;
  setDocumentAttachmentDetails: any;
  data?: any;
  categoryOptions?: any;
};

const PolicyInfoForm: React.FC<PolicyInfoFormProps> = (props) => {
  const {
    disabled,
    setImageAttachmentDetails,
    documentAttachmentDetails,
    imageAttachmentDetails,
    setDocumentAttachmentDetails,
    data,
  } = props;

  const { trigger, setValue, getValues } = useFormContext();
  const [imageAttachmentObject, setImageAttachmentObject] = useState<any>({});
  const [documentsAttachmentObject, setDocumentsAttachmentObject] =
    useState<any>({});

  const { translator } = useLanguageTranslator();

  useEffect(() => {
    if (data?.policy?.picture?.originalFilename) {
      setImageAttachmentObject({
        name: data?.policy?.picture?.originalFilename,
      });
    }
    if (data?.policy?.document?.originalFilename) {
      setDocumentsAttachmentObject({
        name: data?.policy?.document?.originalFilename,
      });
    }
  }, [data?.policy]);

  return (
    <Stack direction={{ xs: "column-reverse", md: "row" }} gap={3}>
      <Stack flex={1} gap={2}>
        <FormControl fullWidth>
          <FormLabel>{translator("Upload Image")}</FormLabel>
          <Attachment
            allowedFormats={["svg", "png", "jpg", "gif", "jpeg"]}
            text="SVG, PNG, JPG or GIF (max. 1 MB)"
            setAttachmentDetails={setImageAttachmentDetails}
            attachmentDetails={imageAttachmentDetails}
            variant={{ variant: "outlined", py: 10 }}
            attachmentObject={imageAttachmentObject}
            setAttachmentObject={setImageAttachmentObject}
          />
        </FormControl>

        <Controller
          name="countryId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormControl fullWidth required error={invalid} disabled={disabled}>
              <FormLabel>{translator("Country")}</FormLabel>
              <Select
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(field.name);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled sx={{ color: "text.secondary" }}>
                  <em>
                    {translator("support.Select")} {translator("Country")}
                  </em>
                </MenuItem>
                {props.countryOptions.map((op, i) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="themeId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth required error={!!error} disabled={disabled}>
              <FormLabel>{translator("Theme")}</FormLabel>
              <Select
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(field.name);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled sx={{ color: "text.secondary" }}>
                  <em>
                    {translator("support.Select")} {translator("Theme")}
                  </em>
                </MenuItem>
                {props.themeOptions.map((op, i) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="geographicalAreaId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth required error={!!error} disabled={disabled}>
              <FormLabel>{translator("generic.Geographical Zone")}</FormLabel>
              <Select
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  setValue("policyCategoryId", "", { shouldValidate: true });
                  trigger(field.name);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled sx={{ color: "text.secondary" }}>
                  {translator("support.Select")}{" "}
                  {translator("generic.Geographical Zone")}
                </MenuItem>
                {props.geographicalAreaOptions.map((op, i) => (
                  <MenuItem key={`${op.value}-${i}`} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="policyCategoryId"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => {
            return (
              <FormControl
                fullWidth
                required
                error={!!error}
                disabled={disabled}
              >
                <FormLabel>
                  {translator("dashboardTableHead.Category")}
                </FormLabel>
                <Select
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger(field.name);
                  }}
                  displayEmpty
                >
                  <MenuItem value="" disabled sx={{ color: "text.secondary" }}>
                    <em>
                      {translator("support.Select")}{" "}
                      {translator("dashboardTableHead.Category")}
                    </em>
                  </MenuItem>
                  {props.categoryOptions.map((op: any, i: any) => (
                    <MenuItem key={`${op.value}-${i}`} value={op.value}>
                      {op.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{error?.message}</FormHelperText>
              </FormControl>
            );
          }}
        />
      </Stack>

      <Stack flex={1} gap={2}>
        <Controller
          name="title"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl required error={!!error} disabled={disabled}>
              <FormLabel>{translator("Policy Name")}</FormLabel>
              <OutlinedInput
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(field.name);
                }}
              />
              <FormHelperText>{error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="description"
          rules={{ required: "Required!" }}
          render={({ field, fieldState: { error } }) => (
            <FormControl required error={!!error} disabled={disabled}>
              <FormLabel>{translator("Policy Description")}</FormLabel>
              <OutlinedInput
                multiline
                rows={3}
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  trigger(field.name);
                }}
              />
              <FormHelperText>{error?.message}</FormHelperText>
            </FormControl>
          )}
        />

        <Controller
          name="date"
          render={({ field }) => (
            <FormControl disabled={disabled} fullWidth>
              <FormLabel>{translator("generic.Date Voted")}</FormLabel>
              <DatePicker
                selected={field?.value}
                onSelect={(date: any) => {
                  if (date) {
                    const formattedDate = dayjs(date).format("YYYY-MM-DD");
                    field.onChange(formattedDate);
                  }
                }}
              />
            </FormControl>
          )}
        />

        <FormControl fullWidth>
          <FormLabel>{translator("Upload document")}</FormLabel>
          <Attachment
            allowedFormats={["pdf"]}
            text="PDF file (max. 50mb)"
            setAttachmentDetails={setDocumentAttachmentDetails}
            attachmentDetails={documentAttachmentDetails}
            variant={{ variant: "outlined" }}
            attachmentObject={documentsAttachmentObject}
            setAttachmentObject={setDocumentsAttachmentObject}
          />
        </FormControl>

        <Controller
          name="documentTitle"
          render={({ field }) => (
            <FormControl disabled={disabled}>
              <FormLabel>{translator(`Document Title`)}</FormLabel>
              <OutlinedInput {...field} />
            </FormControl>
          )}
        />

        <Controller
          name="link"
          render={({ field }) => (
            <FormControl disabled={disabled}>
              <FormLabel>{translator("External Policy Link")}</FormLabel>
              <OutlinedInput {...field} />
            </FormControl>
          )}
        />
      </Stack>
    </Stack>
  );
};

export default PolicyInfoForm;
